type RuntimeConfig = {
  apiBaseUrl?: string;
  devIdentityUserId?: string;
};

const CONFIG_TIMEOUT_MS = 3000;

const CONFIG_RETRY_DELAY_MS = 1000;

const CONFIG_ATTEMPTS = 3;

let config: RuntimeConfig = {};

function parseConfig(parsed: unknown): RuntimeConfig {
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("/config.json must be an object");
  }
  const candidate = parsed as Record<string, unknown>;
  for (const key of ["apiBaseUrl", "devIdentityUserId"]) {
    if (key in candidate && typeof candidate[key] !== "string") {
      throw new Error(`/config.json's ${key} must be a string`);
    }
  }
  const apiBaseUrl = candidate.apiBaseUrl ?? import.meta.env.VITE_API_BASE_URL;
  if (apiBaseUrl) {
    const url = new URL(apiBaseUrl as string);
    if ((url.protocol !== "http:" && url.protocol !== "https:") || url.href !== url.origin + url.pathname) {
      throw new Error("API base must be an HTTP(S) URL without credentials, query or fragment");
    }
  }
  return candidate as RuntimeConfig;
}

async function fetchConfig(timeoutMs: number): Promise<RuntimeConfig> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${import.meta.env.BASE_URL}config.json`, {
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`/config.json answered ${response.status}`);
    return parseConfig(await response.json());
  } catch (error) {
    if (controller.signal.aborted) throw new Error(`/config.json timed out after ${timeoutMs}ms`);
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

async function loadAttempt(attempt: number): Promise<RuntimeConfig> {
  try {
    return await fetchConfig(CONFIG_TIMEOUT_MS * 2 ** attempt);
  } catch (error) {
    const last = attempt + 1 === CONFIG_ATTEMPTS;
    console.warn(
      `runtimeConfig: attempt ${attempt + 1} of ${CONFIG_ATTEMPTS} failed - ${last ? "giving up" : "retrying"}.`,
      error
    );
    if (last) throw new Error("runtimeConfig: /config.json could not be loaded", { cause: error });
    await new Promise((resolve) => setTimeout(resolve, CONFIG_RETRY_DELAY_MS * 2 ** attempt));
    return loadAttempt(attempt + 1);
  }
}

export async function loadRuntimeConfig(): Promise<void> {
  config = await loadAttempt(0);
}

export function runtimeApiBaseUrl(): string | undefined {
  return config.apiBaseUrl;
}

export function runtimeDevIdentityUserId(): string | undefined {
  return config.devIdentityUserId;
}
